import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { TAG_COLORS, type TagColor } from '@chatsdet/ui';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

export type PromptScope = 'global' | 'org';

/**
 * A tag on a prompt. ORG prompts carry real org tags (`id` set, links to the
 * org's tag rows); GLOBAL prompts carry free-string tags (`id` null).
 */
export interface PromptTag {
  id: string | null;
  name: string;
  color: TagColor;
}

/** A prompt as it appears in the list/card view (no body). */
export interface PromptSummary {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  scope: PromptScope;
  tags: PromptTag[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A prompt with its full markdown body. */
export interface PromptDetail extends PromptSummary {
  content: string;
}

export interface PromptInput {
  title: string;
  content: string;
  description?: string | null;
  /** Only honored for superadmins; defaults to an org prompt. */
  scope?: PromptScope;
  /** Free-string tags for global prompts (superadmin scope:'global'). */
  globalTags?: string[];
}

type PromptSummaryPayload = Omit<PromptSummary, 'tags'> & {
  tags: unknown;
};

type PromptDetailPayload = Omit<PromptDetail, 'tags'> & {
  tags: unknown;
};

const tagColors = new Set<string>(Object.keys(TAG_COLORS));

function valueType(value: unknown): string {
  if (Array.isArray(value)) return 'array';
  if (value === null) return 'null';
  return typeof value;
}

function isTagColor(value: unknown): value is TagColor {
  return typeof value === 'string' && tagColors.has(value);
}

function normalizePromptTag(value: unknown): PromptTag | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const record = value as Record<string, unknown>;
  if (
    (typeof record.id !== 'string' && record.id !== null) ||
    typeof record.name !== 'string' ||
    !isTagColor(record.color)
  ) {
    return null;
  }

  return {
    id: record.id,
    name: record.name,
    color: record.color,
  };
}

function captureInvalidPromptTags(
  prompt: { id: string; slug: string; scope: PromptScope },
  tags: unknown,
  invalidTagCount: number
) {
  Sentry.withScope((scope) => {
    scope.setFingerprint(['prompt-library', 'invalid-prompt-tags', valueType(tags)]);
    scope.setTag('feature', 'prompt-library');
    scope.setTag('prompt_library.payload_shape', valueType(tags));
    scope.setTag('prompt_library.scope', prompt.scope);
    scope.setContext('prompt_library', {
      promptId: prompt.id,
      slug: prompt.slug,
      scope: prompt.scope,
      tagsType: valueType(tags),
      tagCount: Array.isArray(tags) ? tags.length : null,
      invalidTagCount,
    });
    Sentry.captureMessage('Prompt library payload had invalid tags', 'warning');
  });
}

export function normalizePromptSummary(prompt: PromptSummaryPayload): PromptSummary {
  if (!Array.isArray(prompt.tags)) {
    captureInvalidPromptTags(prompt, prompt.tags, 1);
    return { ...prompt, tags: [] };
  }

  const tags = prompt.tags.map(normalizePromptTag).filter((tag): tag is PromptTag => tag !== null);
  if (tags.length !== prompt.tags.length) {
    captureInvalidPromptTags(prompt, prompt.tags, prompt.tags.length - tags.length);
  }
  return { ...prompt, tags };
}

export function normalizePromptDetail(prompt: PromptDetailPayload): PromptDetail {
  return normalizePromptSummary(prompt) as PromptDetail;
}

const promptKeys = {
  all: ['prompts'] as const,
  list: () => [...promptKeys.all, 'list'] as const,
  detail: (idOrSlug: string) => [...promptKeys.all, 'detail', idOrSlug] as const,
};

async function api<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: opts?.body ? { 'content-type': 'application/json' } : undefined,
    ...opts,
  });
  if (!res.ok) throw await toApiError(res, `Request failed: ${res.status}`);
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string } & T;
  if (!body.ok) throw new Error(body.error ?? `Request failed: ${res.status}`);
  return body;
}

export function usePrompts() {
  return useQuery({
    queryKey: promptKeys.list(),
    queryFn: () =>
      api<{ data: PromptSummaryPayload[] }>('/prompts').then((body) => ({
        ...body,
        data: body.data.map(normalizePromptSummary),
      })),
    staleTime: 60_000,
  });
}

export function usePrompt(idOrSlug: string | null) {
  return useQuery({
    queryKey: promptKeys.detail(idOrSlug ?? ''),
    queryFn: () =>
      api<{ data: PromptDetailPayload }>(`/prompts/${idOrSlug}`).then((body) => ({
        ...body,
        data: normalizePromptDetail(body.data),
      })),
    enabled: !!idOrSlug,
    staleTime: 60_000,
  });
}

export function useCreatePrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PromptInput) =>
      api<{ data: PromptDetail }>('/prompts', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: promptKeys.all }),
  });
}

export function useUpdatePrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<PromptInput> }) =>
      api<{ data: PromptDetail }>(`/prompts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: promptKeys.all }),
  });
}

export function useDeletePrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/prompts/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: promptKeys.all }),
  });
}
